/* ============================================================================
   UN SOLO REGISTRO

   Había dos: el registro nuevo (`estado:sgsRegistros`) y los cuatro depósitos
   anteriores —reportes ASR, eventos, auditorías, no conformidades—. Eso es un
   error, no una etapa: **un auditor que ve dos listas no sabe cuál es la
   oficial**, y el operador tampoco.

   Este módulo los junta en uno.

   ══════════════════════════════════════════════════════════════════════════
   LAS CINCO REGLAS DE LA MIGRACIÓN

   Son datos reales de un operador. Una migración que pierde algo, o que
   inventa lo que falta, es peor que no migrar.

   1. **No se pierde nada.** Todo campo que no tenga lugar en la estructura
      nueva se conserva en `original`. Si mañana hace falta, está.

   2. **No se inventa nada.** Un registro viejo sin fecha NO recibe la fecha de
      hoy: queda marcado como sin fecha. Inventarla haría creer que el reporte
      es de hoy, y eso desordena cualquier análisis de tendencia.

   3. **Lo migrado se marca como migrado.** Los registros viejos nunca tuvieron
      huella: no se puede afirmar que estén íntegros desde su creación. Se
      firman al migrar y se declara **desde cuándo** la integridad es
      verificable. Fingir lo contrario sería el peor error posible en un
      registro de auditoría.

   4. **Es idempotente.** Correrla dos veces no duplica: cada registro migrado
      conserva su identificador de origen y se reconoce.

   5. **No borra el origen.** La migración copia. Vaciar los depósitos viejos
      es una decisión del operador, posterior y explícita, y solo después de
      exportar.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { huellaDe, ESTADO_INICIAL, ESTADOS } from "./registroSgs.js";

const texto = (v) => (typeof v === "string" ? v.trim() : "");

/** Los depósitos viejos, con el tipo que les corresponde en el registro único. */
export const DEPOSITOS = [
  { clave: "estado:reportesAsr", tipo: "reporte", nombre: "Reportes voluntarios (ASR)" },
  { clave: "estado:eventosSgs", tipo: "evento", nombre: "Eventos indeseables" },
  { clave: "estado:auditorias", tipo: "auditoria", nombre: "Auditorías" },
  { clave: "estado:noConformidades", tipo: "no-conformidad", nombre: "No conformidades" },
];

/**
 * Traduce el estado viejo al canal nuevo.
 *
 * Los estados viejos eran texto libre. Lo que NO se reconoce va al estado
 * inicial, **no a cerrado**: dar por cerrado algo que no se sabe si lo está
 * haría desaparecer trabajo pendiente de la vista del gestor.
 */
export function traducirEstado(viejo) {
  const v = texto(viejo).toLowerCase();
  if (!v) return { estado: ESTADO_INICIAL, reconocido: false };

  if (/cerrad|resuelt|complet/.test(v)) return { estado: "cerrado-sin-accion", reconocido: true };
  if (/convertid/.test(v)) return { estado: "con-accion", reconocido: true };
  if (/an[aá]lisis|proceso|tr[aá]mite|revis/.test(v)) return { estado: "en-analisis", reconocido: true };
  if (/nuevo|abiert|pendient/.test(v)) return { estado: ESTADO_INICIAL, reconocido: true };

  return { estado: ESTADO_INICIAL, reconocido: false };
}

/**
 * Convierte un registro viejo al formato único.
 *
 * @returns {{registro: object|null, avisos: string[]}}
 */
export function convertir(viejo, tipo, { ahora = new Date(), origen = null } = {}) {
  const avisos = [];
  if (!viejo || typeof viejo !== "object") {
    return { registro: null, avisos: ["Registro ilegible: se omite."] };
  }

  /* La descripción es lo único imprescindible: un registro sin ella no se
     puede analizar ni auditar, y migrarlo solo ensucia la lista. */
  const descripcion = texto(viejo.descripcion) || texto(viejo.desc) || texto(viejo.hallazgo);
  if (!descripcion) {
    return { registro: null, avisos: ["Registro sin descripción: no se migra (no sería auditable)."] };
  }

  /* La fecha NO se inventa. Un registro sin fecha queda marcado, porque
     ponerle la de hoy lo haría aparecer como reciente y desordenaría
     cualquier análisis de tendencia. */
  const fecha = texto(viejo.fecha) || texto(viejo.fechaEvento) || null;
  if (!fecha) avisos.push(`«${descripcion.slice(0, 40)}…»: sin fecha en el registro original.`);

  const { estado, reconocido } = traducirEstado(viejo.estado);
  if (viejo.estado && !reconocido) {
    avisos.push(`«${descripcion.slice(0, 40)}…»: el estado «${viejo.estado}» no se reconoció; `
      + "queda como Nuevo para que alguien lo revise.");
  }

  const cuando = (ahora instanceof Date ? ahora : new Date()).toISOString();
  const reportante = texto(viejo.reportante) || null;

  /* Todo lo que no tiene lugar en la estructura nueva se conserva. Si mañana
     hace falta —un peligro, unos precursores, una barrera declarada— está. */
  const conocidos = new Set(["id", "fecha", "fechaEvento", "descripcion", "desc",
    "hallazgo", "estado", "reportante"]);
  const original = {};
  for (const [k, val] of Object.entries(viejo)) {
    if (!conocidos.has(k) && val !== null && val !== undefined && val !== "") original[k] = val;
  }

  const registro = {
    /* El identificador de origen es lo que hace idempotente la migración. */
    id: `mig_${texto(viejo.id) || Math.random().toString(36).slice(2, 10)}`,
    idOriginal: texto(viejo.id) || null,
    depositoOriginal: origen,
    tipo,
    /* Si no hay fecha, se usa la de migración PERO queda dicho que es esa. */
    creado: fecha ? `${fecha}T00:00:00.000Z` : cuando,
    fechaOriginalAusente: !fecha,
    descripcion,
    confidencial: false,
    reportante,
    identidadReservada: null,
    dimensiones: ["sms"],
    rolReportante: null,
    riesgoInicial: null,
    riesgoResidual: null,
    barreras: [],
    estado,
    anotaciones: [],

    /* ═══ LA MARCA QUE NO SE PUEDE QUITAR ═══
       Estos registros nunca tuvieron huella: no se puede afirmar que estén
       íntegros desde su creación. Se firman AHORA, y se declara desde cuándo
       la integridad es verificable. Fingir lo contrario sería el peor error
       posible en un registro de auditoría. */
    migrado: {
      cuando,
      desde: origen,
      nota: "Registro migrado del sistema anterior. Su integridad es verificable "
        + "desde la fecha de migración, no desde su creación: el sistema anterior "
        + "no firmaba los registros.",
    },

    bitacora: [
      {
        cuando: fecha ? `${fecha}T00:00:00.000Z` : cuando,
        quien: reportante || "(sin registrar en el sistema anterior)",
        accion: "creado",
        hacia: ESTADO_INICIAL,
      },
      {
        cuando,
        quien: "(migración automática)",
        accion: "migrado",
        motivo: `Migrado desde ${origen || "el sistema anterior"}`
          + (estado !== ESTADO_INICIAL ? `, estado «${viejo.estado}» → «${ESTADOS[estado]?.etiqueta}»` : ""),
      },
    ],
  };

  if (Object.keys(original).length > 0) registro.original = original;

  registro.huella = huellaDe(registro);
  return { registro, avisos };
}

/**
 * Migra todos los depósitos a una sola lista.
 *
 * **Idempotente:** un registro ya migrado no se vuelve a agregar. Correrla dos
 * veces da el mismo resultado que correrla una.
 *
 * **No borra el origen:** copia. Vaciar los depósitos viejos es una decisión
 * posterior y explícita del operador, y solo después de exportar.
 */
export function migrar(existentes = [], depositos = {}, { ahora = new Date() } = {}) {
  const actuales = Array.isArray(existentes) ? existentes : [];

  /* Lo ya migrado se reconoce por el identificador de origen. */
  const yaMigrados = new Set(
    actuales.map((r) => r?.idOriginal).filter(Boolean),
  );

  const nuevos = [];
  const avisos = [];
  let omitidos = 0;
  let repetidos = 0;

  for (const { clave, tipo, nombre } of DEPOSITOS) {
    const lista = Array.isArray(depositos?.[clave]) ? depositos[clave] : [];

    for (const viejo of lista) {
      const idOrig = texto(viejo?.id);
      if (idOrig && yaMigrados.has(idOrig)) { repetidos += 1; continue; }

      let r;
      try {
        r = convertir(viejo, tipo, { ahora, origen: nombre });
      } catch {
        omitidos += 1;
        avisos.push(`Un registro de «${nombre}» no se pudo convertir y se omitió.`);
        continue;
      }

      avisos.push(...r.avisos);
      if (!r.registro) { omitidos += 1; continue; }

      nuevos.push(r.registro);
      if (idOrig) yaMigrados.add(idOrig);
    }
  }

  return {
    /* Los migrados van PRIMERO: son los más viejos, y la lista se muestra al
       revés. */
    lista: [...nuevos, ...actuales],
    migrados: nuevos.length,
    repetidos,
    omitidos,
    avisos,
    resumen: nuevos.length === 0
      ? (repetidos > 0
        ? `Nada que migrar: los ${repetidos} registros del sistema anterior ya estaban.`
        : "No hay registros en el sistema anterior.")
      : `${nuevos.length} registros migrados`
        + (repetidos > 0 ? `, ${repetidos} ya estaban` : "")
        + (omitidos > 0 ? `, ${omitidos} omitidos por falta de datos` : "") + ".",
  };
}

/**
 * ¿Hay algo pendiente de migrar?
 *
 * Se usa para avisar en pantalla. Mientras haya dos listas, el operador tiene
 * que saberlo.
 */
export function hayPendientes(existentes = [], depositos = {}) {
  const actuales = Array.isArray(existentes) ? existentes : [];
  const yaMigrados = new Set(actuales.map((r) => r?.idOriginal).filter(Boolean));

  let pendientes = 0;
  for (const { clave } of DEPOSITOS) {
    const lista = Array.isArray(depositos?.[clave]) ? depositos[clave] : [];
    for (const viejo of lista) {
      const id = texto(viejo?.id);
      if (!id || !yaMigrados.has(id)) pendientes += 1;
    }
  }
  return pendientes;
}
